import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Linkedin, Twitter, Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import { companyInfo } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-950 text-gray-300 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_hvgtec-home/artifacts/8p3d2ihe_HVGTEC%20AI%20Logo.png" 
                alt="HVGTEC Logo" 
                className="h-10 w-10"
              />
              <span className="text-xl font-bold text-white">
                HVG<span className="text-cyan-400">TEC</span>
              </span>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">
              Bridging the digital divide with carrier-grade ISP solutions and solar-powered infrastructure.
            </p>
            <div className="flex space-x-3">
              <a 
                href={companyInfo.socialMedia.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 bg-slate-800 rounded-lg hover:bg-blue-600 transition-colors duration-200"
                title="Follow us on Facebook"
              >
                <Facebook size={18} />
              </a>
              <a 
                href={companyInfo.messenger} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 bg-slate-800 rounded-lg hover:bg-blue-600 transition-colors duration-200"
                title="Chat with us on Messenger"
              >
                <MessageCircle size={18} />
              </a>
              <a 
                href={companyInfo.socialMedia.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 bg-slate-800 rounded-lg hover:bg-cyan-500 transition-colors duration-200"
                title="Connect on LinkedIn"
              >
                <Linkedin size={18} />
              </a>
              <a 
                href={companyInfo.socialMedia.twitter} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 bg-slate-800 rounded-lg hover:bg-cyan-500 transition-colors duration-200"
                title="Follow us on Twitter"
              >
                <Twitter size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="text-sm hover:text-cyan-400 transition-colors">Home</Link></li>
              <li><Link to="/about" className="text-sm hover:text-cyan-400 transition-colors">About Us</Link></li>
              <li><Link to="/services" className="text-sm hover:text-cyan-400 transition-colors">Services</Link></li>
              <li><Link to="/franchise" className="text-sm hover:text-cyan-400 transition-colors">Franchise</Link></li>
              <li><Link to="/resilience" className="text-sm hover:text-cyan-400 transition-colors">Network Resilience</Link></li>
              <li><Link to="/hardware" className="text-sm hover:text-cyan-400 transition-colors">Hardware</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-semibold mb-4">Our Services</h3>
            <ul className="space-y-2 text-sm">
              <li className="hover:text-cyan-400 transition-colors cursor-pointer">FTTH Infrastructure</li>
              <li className="hover:text-cyan-400 transition-colors cursor-pointer">Headend-in-a-Box</li>
              <li className="hover:text-cyan-400 transition-colors cursor-pointer">Network Architecture</li>
              <li className="hover:text-cyan-400 transition-colors cursor-pointer">Solar Micro POPs</li>
              <li className="hover:text-cyan-400 transition-colors cursor-pointer">Systems Integration</li>
              <li className="hover:text-cyan-400 transition-colors cursor-pointer">ISP Consultation</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{companyInfo.location}</span>
              </li>
              <li className="flex items-start space-x-3">
                <Mail size={18} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                <a href={`mailto:${companyInfo.email}`} className="text-sm hover:text-cyan-400 transition-colors">
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Phone size={18} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                <a href={`tel:${companyInfo.phone}`} className="text-sm hover:text-cyan-400 transition-colors">
                  {companyInfo.phone}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MessageCircle size={18} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <a href={`https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}`} 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="hover:text-cyan-400 transition-colors">WhatsApp</a>
                  <span className="text-gray-500 mx-2">|</span>
                  <a href={companyInfo.telegram} 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="hover:text-cyan-400 transition-colors">Telegram</a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Technology Partners */}
        <div className="py-8 border-t border-slate-800">
          <p className="text-center text-sm text-gray-500 mb-6">Our Technology Partners</p>
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {/* Ubiquiti */}
            <a href="https://ui.com" target="_blank" rel="noopener noreferrer" 
               className="group flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-cyan-500/50 transition-all" 
               title="Ubiquiti">
              <svg className="w-5 h-5 text-gray-400 group-hover:text-cyan-400 transition-colors" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                <circle cx="12" cy="12" r="4" fill="currentColor"/>
              </svg>
              <span className="text-sm font-semibold text-gray-400 group-hover:text-cyan-400 transition-colors">Ubiquiti</span>
            </a>
            {/* MikroTik */}
            <a href="https://mikrotik.com" target="_blank" rel="noopener noreferrer" 
               className="group flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-orange-500/50 transition-all" 
               title="MikroTik">
              <svg className="w-5 h-5 text-gray-400 group-hover:text-orange-400 transition-colors" viewBox="0 0 24 24" fill="currentColor">
                <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
                <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <span className="text-sm font-semibold text-gray-400 group-hover:text-orange-400 transition-colors">MikroTik</span>
            </a>
            {/* Juniper */}
            <a href="https://juniper.net" target="_blank" rel="noopener noreferrer" 
               className="group flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-green-500/50 transition-all" 
               title="Juniper Networks">
              <svg className="w-5 h-5 text-gray-400 group-hover:text-green-400 transition-colors" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L4 7v10l8 5 8-5V7l-8-5z" stroke="currentColor" strokeWidth="2" fill="none"/>
                <circle cx="12" cy="12" r="3" fill="currentColor"/>
              </svg>
              <span className="text-sm font-semibold text-gray-400 group-hover:text-green-400 transition-colors">Juniper</span>
            </a>
            {/* Dahua */}
            <a href="https://dahuasecurity.com" target="_blank" rel="noopener noreferrer" 
               className="group flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-red-500/50 transition-all" 
               title="Dahua Technology">
              <svg className="w-5 h-5 text-gray-400 group-hover:text-red-400 transition-colors" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none"/>
                <circle cx="12" cy="12" r="3" fill="currentColor"/>
                <path d="M12 5v2M12 17v2M5 12h2M17 12h2" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <span className="text-sm font-semibold text-gray-400 group-hover:text-red-400 transition-colors">Dahua</span>
            </a>
            {/* Hikvision */}
            <a href="https://hikvision.com" target="_blank" rel="noopener noreferrer" 
               className="group flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-red-500/50 transition-all" 
               title="Hikvision">
              <svg className="w-5 h-5 text-gray-400 group-hover:text-red-400 transition-colors" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5z" stroke="currentColor" strokeWidth="2" fill="none"/>
                <circle cx="12" cy="12" r="3" fill="currentColor"/>
              </svg>
              <span className="text-sm font-semibold text-gray-400 group-hover:text-red-400 transition-colors">Hikvision</span>
            </a>
            {/* Starlink */}
            <a href="https://starlink.com" target="_blank" rel="noopener noreferrer" 
               className="group flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-blue-500/50 transition-all" 
               title="Starlink">
              <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 3L3 8l9 5 9-5-9-5z"/>
                <path d="M3 16l9 5 9-5"/>
                <path d="M3 12l9 5 9-5"/>
              </svg>
              <span className="text-sm font-semibold text-gray-400 group-hover:text-blue-400 transition-colors">Starlink</span>
            </a>
            {/* Mimosa */}
            <a href="https://mimosa.co" target="_blank" rel="noopener noreferrer" 
               className="group flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-purple-500/50 transition-all" 
               title="Mimosa Networks">
              <svg className="w-5 h-5 text-gray-400 group-hover:text-purple-400 transition-colors" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none"/>
                <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2" fill="none"/>
                <circle cx="12" cy="12" r="2" fill="currentColor"/>
              </svg>
              <span className="text-sm font-semibold text-gray-400 group-hover:text-purple-400 transition-colors">Mimosa</span>
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-sm text-gray-500 mb-1">
                © {currentYear} HVGTEC. All rights reserved.
              </p>
              <p className="text-xs text-cyan-400 font-medium">
                Network Architecture: OSPF & VPLS Enabled | Powered by HVGTEC Carrier-Grade Infrastructure
              </p>
            </div>
            <div className="flex space-x-6 text-sm text-gray-500">
              <a href="/terms" className="hover:text-cyan-400 transition-colors">Privacy Policy</a>
              <a href="/terms" className="hover:text-cyan-400 transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-cyan-400 transition-colors">Documentation</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
